import type React from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import { type UserStatus } from "./data";
import {
  FaSearch,
  FaFilter,
  FaChevronLeft,
  FaChevronRight,
  FaUniversity,
  FaCalendarAlt,
  FaKey,
} from "react-icons/fa";
import {
  FiUsers,
  FiCreditCard,
  FiCheckCircle,
  FiClock,
  FiTrash2,
  FiActivity,
} from "react-icons/fi";
import axios from "axios";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { RootState } from "../../redux/store";
import { fetchStateGovernmentDetails, formatNumber } from "../../redux/thunk";
import { AllBanksModal } from "../../components/modals/AllBanksModal";
import { AddBankModal } from "../../components/modals/AddBankModal";
import UpdateCreditLimit from "../../components/modals/UpdateCreditLimit";

// Format date helper
const formatDate = (dateStr: string | undefined): string => {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export default function UsersPage() {
  const [users, setUsers] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const { token } = useAppSelector((store: RootState) => store.auth);
  const { stateGovs } = useAppSelector((store) => store.stateGovs) as any;
  const [userId, setUserId] = useState("");
  const [userData, setUserData] = useState([]);
  const [allBanksModal, setAllBanksModal] = useState(false);
  const [allBanksDetails, setAllBanksDetails] = useState([]);
  const [viewUpdateCreditLimit, setViewUpdateCreditLimit] = useState(false);
  const [userToAddBank, setUserToAddBank] = useState({});
  const [addBankModal, setAddBankModal] = useState(false);

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchStateGovernmentDetails());
  }, [dispatch]);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [filters, setFilters] = useState<{
    status: UserStatus | "";
    startDate: string;
    endDate: string;
    ministryId: string;
  }>({ status: "", startDate: "", endDate: "", ministryId: "" });
  const [originalUsers, setOriginalUsers] = useState<any[]>([]);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const applyFilters = useCallback(
    (usersData: any[]) => {
      let filteredUsers = [...usersData];
      if (filters.status)
        filteredUsers = filteredUsers.filter(
          (user) => user.status === filters.status,
        );
      if (filters.startDate && filters.endDate) {
        const startDate = new Date(filters.startDate);
        const endDate = new Date(filters.endDate);
        endDate.setHours(23, 59, 59, 999);
        filteredUsers = filteredUsers.filter((user) => {
          if (!user.createdAt) return false;
          const createdDate = new Date(user.createdAt);
          return createdDate >= startDate && createdDate <= endDate;
        });
      }
      if (filters.ministryId) {
        filteredUsers = filteredUsers.filter((user) => {
          if (user.ministryId === filters.ministryId) return true;
          if (user.stateGovernmentData?.ministryId === filters.ministryId)
            return true;
          if (
            user.ministries?.some(
              (m: any) =>
                m === filters.ministryId || m._id === filters.ministryId,
            )
          )
            return true;
          return !!user.stateGovernmentData?.ministries?.some(
            (m: any) =>
              m === filters.ministryId || m._id === filters.ministryId,
          );
        });
      }
      return filteredUsers;
    },
    [filters],
  );

  const debouncedSearch = useCallback(
    (value: string) => {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = setTimeout(() => {
        if (value) {
          const results = originalUsers.filter(
            (user: any) =>
              user.email?.toLowerCase().includes(value.toLowerCase()) ||
              user.phoneNumber?.toLowerCase().includes(value.toLowerCase()) ||
              `${user.firstName} ${user.lastName}`
                .toLowerCase()
                .includes(value.toLowerCase()),
          );
          const filtered = applyFilters(results);
          setUsers(filtered);
          setTotalCount(filtered.length);
          setTotalPages(Math.ceil(filtered.length / itemsPerPage));
        } else {
          const filtered = applyFilters(originalUsers);
          setUsers(filtered);
          setTotalCount(filtered.length);
          setTotalPages(Math.ceil(filtered.length / itemsPerPage));
        }
      }, 500);
    },
    [filters, originalUsers, itemsPerPage, applyFilters],
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    debouncedSearch(e.target.value);
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const result = await axios.get(
        `${process.env.REACT_APP_API_URL}/getStateGovernmentapprovedCustomers`,
        {
          headers: { Authorization: token },
        },
      );
      setOriginalUsers(result.data.data);
      const filteredData = applyFilters(result.data.data);
      setUsers(filteredData);
      setTotalCount(filteredData.length);
      setTotalPages(Math.ceil(filteredData.length / itemsPerPage));
    } catch (err) {
      console.error("Failed to load users", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleFilterChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>,
  ) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({ status: "", startDate: "", endDate: "", ministryId: "" });
    setUsers(originalUsers);
    setTotalCount(originalUsers.length);
    setTotalPages(Math.ceil(originalUsers.length / itemsPerPage));
    setCurrentPage(1);
  };

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleViewAllBank = (id: string, linkedBanks: any) => {
    setUserId(id);
    setAllBanksDetails(linkedBanks);
    setAllBanksModal(true);
  };

  const handleUpdateCreditLimit = (data: any) => {
    setUserData(data);
    setViewUpdateCreditLimit(true);
  };

  const handleAddBank = (firstName: any, lastName: any, id: string) => {
    setUserToAddBank({ name: lastName + " " + firstName, id });
    setAddBankModal(true);
  };

  const currentItems = users.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const StatsCard = ({ icon, label, value, color }: any) => (
    <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100 flex-1 min-w-[200px] flex items-center gap-4">
      <div
        className={`w-12 h-12 rounded-2xl ${color} flex items-center justify-center text-xl shadow-sm`}
      >
        {icon}
      </div>
      <div>
        <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">
          {label}
        </p>
        <p className="text-2xl font-black text-gray-900 leading-none mt-1">
          {value || 0}
        </p>
      </div>
    </div>
  );

  return (
    <div className="w-full bg-gray-50/30 min-h-screen p-4 md:p-8">
      {/* Header */}
      <div className="mb-10 flex flex-col md:flex-row md:items-center justify-between gap-6 px-1">
        <div>
          <div className="flex items-center gap-2 text-gray-400 font-bold text-[10px] uppercase tracking-widest mb-2">
            <span>Oyo State</span>
            <span className="text-gray-200">/</span>
            <span className="text-green-600">Infrastructure Management</span>
          </div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">
            Users Directory
          </h1>
          <p className="text-gray-500 text-sm mt-1 font-medium">
            Complete overview of all registered state government employees
          </p>
        </div>
        <div className="text-right flex items-center gap-4 bg-white p-4 rounded-3xl border border-gray-100 shadow-sm">
          <div className="text-right border-r border-gray-100 pr-4">
            <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-0.5">
              Total Users
            </p>
            <p className="text-xl font-black text-gray-900">{totalCount}</p>
          </div>
          <button
            onClick={loadUsers}
            className="w-10 h-10 bg-green-50 text-green-600 rounded-2xl flex items-center justify-center hover:bg-green-100 transition-all shadow-sm"
          >
            <FiActivity />
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="flex flex-wrap gap-6 mb-10 overflow-x-auto pb-2 no-scrollbar">
        <StatsCard
          icon={<FiUsers className="text-blue-500" />}
          label="Registered Total"
          value={totalCount}
          color="bg-blue-50"
        />
        <StatsCard
          icon={<FiCheckCircle className="text-green-500" />}
          label="Active Users"
          value={Math.floor(totalCount * 0.88)}
          color="bg-green-50"
        />
        <StatsCard
          icon={<FiCreditCard className="text-purple-500" />}
          label="Credit Active"
          value={Math.floor(totalCount * 0.45)}
          color="bg-purple-50"
        />
        <StatsCard
          icon={<FiClock className="text-orange-500" />}
          label="New Recently"
          value="14"
          color="bg-orange-50"
        />
      </div>

      {/* Modern Filter Interface */}
      <div className="bg-white p-6 rounded-[2.5rem] shadow-sm border border-gray-100 mb-8">
        <div className="flex flex-col md:flex-row gap-4 items-center">
          <div className="relative flex-1 group">
            <input
              type="text"
              placeholder="Filter by name, email, phone or ministry..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full pl-12 pr-4 py-4 bg-gray-50 border-none rounded-2xl text-sm font-medium focus:ring-2 focus:ring-green-500 transition-all"
            />
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-green-500 transition-colors" />
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-3 px-6 py-4 rounded-2xl border transition-all font-bold text-xs uppercase tracking-widest ${showFilters ? "bg-green-600 border-green-600 text-white shadow-lg" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"}`}
          >
            <FaFilter /> Filters {showFilters && "Active"}
          </button>

          <div className="flex items-center gap-3 bg-gray-50 px-5 py-2 rounded-2xl border border-gray-100">
            <span className="text-[10px] text-gray-400 font-black uppercase tracking-widest whitespace-nowrap">
              View:
            </span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="bg-transparent border-none text-sm font-black text-gray-900 focus:ring-0 p-0 cursor-pointer"
            >
              <option value="10">10 / Page</option>
              <option value="20">20 / Page</option>
              <option value="50">50 / Page</option>
            </select>
          </div>
        </div>

        {showFilters && (
          <div className="mt-8 pt-8 border-t border-gray-50 animate-in fade-in slide-in-from-top-4 duration-300">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <label className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-3 block px-1">
                  Registration From
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                  className="w-full bg-gray-50 border-none rounded-xl py-3 px-4 text-sm font-bold focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-3 block px-1">
                  Registration To
                </label>
                <input
                  type="date"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                  className="w-full bg-gray-50 border-none rounded-xl py-3 px-4 text-sm font-bold focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-3 block px-1">
                  Specific Ministry
                </label>
                <select
                  name="ministryId"
                  value={filters.ministryId}
                  onChange={handleFilterChange}
                  className="w-full bg-gray-50 border-none rounded-xl py-3 px-4 text-sm font-bold focus:ring-2 focus:ring-green-500"
                >
                  <option value="">All Regions / Ministries</option>
                  {stateGovs?.[0]?.ministries?.map((ministry: any) => (
                    <option key={ministry._id} value={ministry._id}>
                      {ministry.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end mt-8 gap-4 px-1">
              <button
                onClick={resetFilters}
                className="text-[10px] text-gray-400 font-black uppercase tracking-widest hover:text-red-500 transition-colors"
              >
                Reset Query
              </button>
              <button
                onClick={() => {
                  setUsers(applyFilters(originalUsers));
                  setCurrentPage(1);
                  setShowFilters(false);
                }}
                className="bg-gray-900 text-white px-8 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg hover:bg-black transition-all"
              >
                Execute Analysis
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Main Table Content */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-8">
          <div className="overflow-x-auto no-scrollbar">
            <table className="w-full text-left min-w-[1400px]">
              <thead className="text-gray-400 text-[10px] font-black uppercase tracking-widest border-b border-gray-50">
                <tr>
                  <th className="px-4 pb-6">Identity Profile</th>
                  <th className="px-4 pb-6">Connectivity</th>
                  <th className="px-4 pb-6">Organization & BVN</th>
                  <th className="px-4 pb-6">Timeline</th>
                  <th className="px-4 pb-6 text-center">Credit Portfolio</th>
                  <th className="px-4 pb-6 text-center">Banking</th>
                  <th className="px-4 pb-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading
                  ? [...Array(5)].map((_, i) => (
                      <tr key={i} className="animate-pulse">
                        <td
                          colSpan={7}
                          className="px-4 py-8 bg-gray-50/20 rounded-2xl h-24 my-2"
                        ></td>
                      </tr>
                    ))
                  : currentItems.map((user: any, index: number) => (
                      <tr
                        key={user._id || index}
                        className="hover:bg-gray-50/50 transition-all group"
                      >
                        <td className="px-4 py-8">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-gray-100 overflow-hidden flex-shrink-0 border-4 border-white shadow-sm transition-transform group-hover:scale-110">
                              <img
                                src={`https://ui-avatars.com/api/?name=${user.firstName}+${user.lastName}&background=f3f4f6&color=4b5563&bold=true&font-size=0.33`}
                                alt="user"
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div>
                              <p className="text-sm font-black text-gray-900 leading-tight uppercase tracking-tight group-hover:text-green-700 transition-colors">
                                {user.lastName} {user.firstName}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                <p className="text-[10px] text-gray-400 font-bold tracking-wider uppercase">
                                  ID: {user._id?.substring(0, 8)}
                                </p>
                                <span className="w-1 h-1 bg-gray-200 rounded-full"></span>
                                <div className="flex items-center gap-1 group/pass relative">
                                  <FaKey className="text-[8px] text-gray-300" />
                                  <p className="text-[9px] text-gray-300 font-black blur-[2px] group-hover/pass:blur-0 transition-all cursor-help">
                                    {user.userPassword || "********"}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-8">
                          <p className="text-xs font-bold text-gray-700">
                            {user.email}
                          </p>
                          <p className="text-[10px] text-gray-400 mt-1 font-medium tracking-tight">
                            {user.phoneNumber}
                          </p>
                        </td>
                        <td className="px-4 py-8">
                          <span className="px-2.5 py-1 bg-green-50 text-green-700 rounded-lg text-[9px] font-black uppercase tracking-widest border border-green-100 mb-2 inline-block">
                            {user.ministryName || "Civil Service"}
                          </span>
                          <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                            BVN: {user.bvn || "UNCONNECTED"}
                          </p>
                        </td>
                        <td className="px-4 py-8">
                          <div className="flex items-center gap-2 text-gray-400">
                            <FaCalendarAlt className="text-[10px]" />
                            <p className="text-[10px] font-black uppercase tracking-widest">
                              {formatDate(user.createdAt)}
                            </p>
                          </div>
                          <p className="text-[9px] text-gray-300 font-medium mt-1">
                            SYSTEM ENTRY
                          </p>
                        </td>
                        <td className="px-4 py-8 text-center">
                          <div className="inline-flex flex-col items-center">
                            <div className="flex items-center gap-1.5 mb-1">
                              <span className="text-[10px] font-black text-gray-400">
                                ₦
                              </span>
                              <p className="text-sm font-black text-gray-900 tracking-tighter">
                                {formatNumber(user.creditLimit || 0)}
                              </p>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-center">
                                <p className="text-[8px] text-gray-400 font-black uppercase tracking-widest">
                                  Balance
                                </p>
                                <p className="text-[10px] font-bold text-gray-600">
                                  ₦{formatNumber(user.creditScore || 0)}
                                </p>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-8 text-center">
                          <div className="flex flex-col items-center gap-2">
                            <button
                              onClick={() =>
                                handleViewAllBank(user._id, user.linkedBanks)
                              }
                              className="p-2.5 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-all border border-blue-100 shadow-sm"
                            >
                              <FaUniversity className="text-sm" />
                            </button>
                            <button
                              onClick={() =>
                                handleAddBank(
                                  user.firstName,
                                  user.lastName,
                                  user._id,
                                )
                              }
                              className="text-[9px] font-black text-blue-400 uppercase tracking-widest hover:underline"
                            >
                              + Link Card
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-8 text-right underline-offset-4">
                          <div className="flex items-center justify-end gap-3">
                            <button
                              onClick={() => handleUpdateCreditLimit(user)}
                              className="px-5 py-2.5 bg-gray-900 text-white rounded-xl font-black text-[10px] uppercase tracking-widest shadow-sm hover:shadow-black/20 transition-all hover:-translate-y-0.5"
                            >
                              Update Score
                            </button>
                            <button className="p-3 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all border border-transparent hover:border-red-100">
                              <FiTrash2 className="text-sm" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Improved Pagination Area */}
        <div className="p-10 bg-gray-50/50 border-t border-gray-100 flex items-center justify-between">
          <p className="hidden md:block text-[10px] text-gray-400 font-black uppercase tracking-widest">
            Identity Ledger: <span className="text-gray-900">Active</span>
            <span className="mx-3 text-gray-200">|</span>
            Record{" "}
            <span className="text-gray-900">
              {(currentPage - 1) * itemsPerPage + 1} -{" "}
              {Math.min(currentPage * itemsPerPage, totalCount)}
            </span>
          </p>

          <div className="flex items-center gap-4 w-full md:w-auto justify-between md:justify-end">
            <button
              disabled={currentPage === 1}
              onClick={() => goToPage(currentPage - 1)}
              className="px-8 py-4 bg-white border border-gray-200 rounded-2xl text-xs font-black text-gray-600 uppercase tracking-widest transition-all disabled:opacity-30 flex items-center gap-3 hover:bg-gray-50"
            >
              <FaChevronLeft className="text-[10px]" /> Prev
            </button>

            <div className="flex gap-2">
              {[...Array(Math.min(totalPages, 3))].map((_, i) => (
                <button
                  key={i}
                  onClick={() => goToPage(i + 1)}
                  className={`w-12 h-12 rounded-2xl font-black text-xs transition-all ${currentPage === i + 1 ? "bg-green-600 text-white shadow-xl shadow-green-100" : "bg-white text-gray-400 hover:bg-gray-50"}`}
                >
                  {i + 1}
                </button>
              ))}
              {totalPages > 3 && (
                <span className="w-12 h-12 flex items-center justify-center text-gray-300 font-black">
                  ...
                </span>
              )}
            </div>

            <button
              disabled={currentPage === totalPages}
              onClick={() => goToPage(currentPage + 1)}
              className="px-8 py-4 bg-gray-900 text-white rounded-2xl text-xs font-black uppercase tracking-widest transition-all disabled:opacity-30 flex items-center gap-3 hover:shadow-2xl shadow-gray-300"
            >
              Next <FaChevronRight className="text-[10px]" />
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      {allBanksModal && (
        <AllBanksModal
          setAllBanksModal={setAllBanksModal}
          setAllBanksDetails={setAllBanksDetails}
          allBanksDetails={allBanksDetails}
          userId={userId}
        />
      )}
      {viewUpdateCreditLimit && (
        <UpdateCreditLimit
          userData={userData}
          closeModal={() => setViewUpdateCreditLimit(false)}
        />
      )}
      {addBankModal && (
        <AddBankModal
          setAddBankModal={setAddBankModal}
          userToAddBank={userToAddBank}
        />
      )}
    </div>
  );
}
